import { Box, Button, Container, Flex, Text, Badge } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import moment from "moment";
import { useState, useMemo, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import AppBreadcrumb from "components/AppBreadcrumb";
import { report_api } from "./report.service";
import { showErrorToast, showSuccessToast } from "utilities/Toast";
import { IconDownload, IconPrinter } from "@tabler/icons-react";

export default function StockTransferReport() {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

  const printRef = useRef();


  // Fetch API
  const fetchReport = async () => {

    if (!startDate) {
      showErrorToast({
        title: "Error",
        message: "Select start date",
      });
      return;
    }

    setLoading(true);

    try {
      const params = new URLSearchParams({
        start_date: moment(startDate).format("YYYY-MM-DD"),
        end_date: moment(endDate || startDate).format("YYYY-MM-DD"), // 🔥 fix
      }).toString();

      const response = await report_api.get_stock_transfer_report(params);

      if (!response.success) {
        showErrorToast({ title: "Error", message: "Failed" });
        return;
      }

      setTableData(response.data || []);
    } catch (err) {
      showErrorToast({ title: "Error", message: "API Failed" });
    } finally {
      setLoading(false);
    }
  };

  // Columns
  const columns = useMemo(() => [
    {
      header: "Stock ID",
      accessorKey: "id",
      size: 80,
    },
    {
      header: "Date",
      accessorKey: "date",
    },
    {
      header: "From Counter",
      accessorKey: "from_counter",
    },
    {
      header: "To Counter",
      accessorKey: "to_counter",
    },
    {
      header: "Product",
      accessorKey: "product",
    },
    {
      header: "Batch",
      accessorKey: "batch",
    },
    {
      header: "Original Qty",
      accessorKey: "original_qty",
      Cell: ({ cell }) => parseFloat(cell.getValue() || 0).toFixed(3),
    },
    {
      header: "Received Qty",
      accessorKey: "received_qty",
      Cell: ({ cell }) => parseFloat(cell.getValue() || 0).toFixed(3),
    },
    {
      header: "Status",
      accessorKey: "status",
      Cell: ({ cell, row }) => {
        const status = cell.getValue();
        const originalId = row.original.original_stock_id;
        if (status === "loss_report" && originalId) {
          return (
            <Badge color="grape" sx={{ cursor: "help" }} title={`Created from Transfer #${originalId}`}>
              {status}
            </Badge>
          );
        }
        return <Badge color={status === "rejected" ? "red" : "green"}> {status}</Badge>;
      },
    },
  ], []);

  const idCounts = useMemo(() => {
    const counts = {};
    tableData.forEach((row) => {
      counts[row.id] = (counts[row.id] || 0) + 1;
    });
    return counts;
  }, [tableData]);

  const idColorMap = useMemo(() => {
    const map = {};
    let index = 0;

    tableData.forEach((row) => {
      if (idCounts[row.id] > 1 && !map[row.id]) {
        map[row.id] = index++;
      }
    });

    return map;
  }, [tableData, idCounts]);

  const rowColors = [
    "#f9e8c5ff",
    "#aef1dcff",
    "#a0cbeaff",
    "#d3caf6ff",
    "#eab9cdff",
  ];

  const table = useMantineReactTable({
    columns,
    data: tableData,
    state: { isLoading: loading },
    enableStickyHeader: true,

    mantineTableContainerProps: {
      style: { maxHeight: "400px", overflowY: "auto" },
    },

    mantineTableBodyCellProps: ({ row }) => {
      const stockId = row.original.id;

      // ❌ If only 1 row → no color
      if (idCounts[stockId] <= 1) return {};

      const colorIndex = idColorMap[stockId];

      return {
        sx: {
          backgroundColor: `${rowColors[colorIndex % rowColors.length]} !important`,
        },
      };
    },
  });

  const filteredRows = table.getFilteredRowModel().rows;

  const filteredTotal = filteredRows.reduce(
    (sum, row) => sum + (parseFloat(row.original.received_qty) || 0),
    0
  );

  // CSV Download
  const downloadCSV = () => {
    if (tableData.length === 0) {
      showErrorToast({ title: "Error", message: "No Data" });
      return;
    }

    const rows = [
      ["Stock Transfer Report"],
      [`From: ${moment(startDate).format("DD/MM/YYYY")}`],
      [`To: ${moment(endDate).format("DD/MM/YYYY")}`],
      [],
      ["Date", "From", "To", "Product", "Batch", "Original Qty", "Received Qty", "Status"],
      ...tableData.map((i) => [
        i.id,
        i.date,
        i.from_counter,
        i.to_counter,
        i.product,
        i.batch,
        i.original_qty,
        i.received_qty,
        i.status,
      ]),
      [],
    ];

    const csv =
      "data:text/csv;charset=utf-8," +
      rows.map((e) => e.join(",")).join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = `StockTransfer_${moment().format("DDMMYYYY")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showSuccessToast({ title: "Success", message: "Downloaded" });
  };

  // Print
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  const PrintComponent = () => (
    <div ref={printRef} style={{ padding: 20 }}>
      <h2 style={{ textAlign: "center" }}>Stock Transfer Report</h2>

      <p style={{ textAlign: "center" }}>
        {moment(startDate).format("DD/MM/YYYY")} -{" "}
        {moment(endDate).format("DD/MM/YYYY")}
      </p>

      <table border="1" cellPadding="6" style={{ width: "100%" }}>
        <thead>
          <tr>
            <th>Date</th>
            <th>From</th>
            <th>To</th>
            <th>Product</th>
            <th>Batch</th>
            <th>Original Qty</th>
            <th>Received Qty</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {tableData.map((i, idx) => (
            <tr key={idx}>
              <td>{i.date}</td>
              <td>{i.from_counter}</td>
              <td>{i.to_counter}</td>
              <td>{i.product}</td>
              <td>{i.batch}</td>
              <td>{i.original_qty}</td>
              <td>{i.received_qty}</td>
              <td>{i.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Container fluid>
      <div style={{ display: "none" }}>
        <PrintComponent />
      </div>

      <Box p={25}>
        <Flex justify="space-between" align="end">
          <Box>
            <Text size="lg" fw={600}>
              Stock Transfer Report
            </Text>
            <AppBreadcrumb />
          </Box>

          <Flex gap={10} align="flex-end" wrap="wrap">
            <Button size="sm" leftIcon={<IconDownload />} onClick={downloadCSV}>
              CSV
            </Button>

            <Button size="sm" leftIcon={<IconPrinter />} onClick={handlePrint}>
              Print
            </Button>

            <DatePickerInput
              label="Start Date"
              value={startDate}
              onChange={setStartDate}
              style={{ width: "200px" }}
            />

            <DatePickerInput
              label="End Date"
              value={endDate}
              onChange={setEndDate}
              disabled={!startDate}
              style={{ width: "200px" }}
            />

            <Button size="sm" color="green" onClick={fetchReport}>
              Search
            </Button>
          </Flex>
        </Flex>
      </Box>

      <Box px={25}>
        <MantineReactTable table={table} />
      </Box>

      {/* Footer Totals */}
      <Box
        p={15}
        style={{
          position: "fixed",
          bottom: 0,
          width: "100%",
          background: "#f1f3f5",
        }}
      >
      </Box>
    </Container>
  );
}